import type { Company } from './companies';

// Frontend mirror of src/assessment-modules/profile-questions.ts (the server
// is the source of truth). Keep the text in sync if the backend copy changes.

export const PROFILE_QUESTION_KEYS = [
  'a01', 'a02', 'a03', 'a04', 'a05', 'a06', 'a07',
  'a08', 'a09', 'a10', 'a11', 'a12', 'a13',
] as const;

export type ProfileQuestionKey = (typeof PROFILE_QUESTION_KEYS)[number];

export const PROFILE_QUESTIONS: Record<ProfileQuestionKey, string> = {
  a01: 'ATF: Does your company import items subject to the ATF regulations?',
  a02:
    'ITAR: Does your company export, import, sell, lease, manufacture or otherwise possesses ITAR-controlled defense articles and/or technical data, provide ITAR-controlled defense services, and/or engage in ITAR-controlled brokering activities?',
  a03: 'ITAR Agreements: Is your company a party to DDTC approved agreement?',
  a04:
    'NISPOM: Does your company handle national security classified (i.e., SECRET, TOP SECRET, etc.) information?',
  a05:
    'DOD CYBER: Does your company have U.S. government contracts that involve covered defense information or operationally critical support as defined in DFARS Clause 252.204-7012, are/or U.S. government contracts that are not solely for the acquisition of commercial items?',
  a06:
    'PHYSICAL EXPORTS: Does your company make physical exports of hardware, software, or technical information?',
  a07: 'ROUTED: Is your company involved in routed export transactions?',
  a08: 'ROUTED: Is your company in the business of freight forwarding?',
  a09:
    'PHYSICAL SHIPMENTS: Does your company make physical shipments of items and/or technical information to and/or from foreign locations?',
  a10: 'HAND CARRIES: Do your employees engage in international travel?',
  a11:
    'EXPORT CONTROLLED INFORMATION: Does the company create, receive, store or otherwise maintain export-controlled software and/or technical information, to include hardware in a state that reveals export controlled technical information?',
  a12: 'FOREIGN BRANCHES/SUBSIDIARIES: Does your company have foreign branches or subsidiaries?',
  a13: 'FOREIGN PERSONS: Does your company employ foreign persons?',
};

// Plain-language "how to tell" guidance for the questions whose answer isn't
// self-evident. Illustrative only — not legal advice; the UI pairs this with the
// "Don't know" → consult-counsel flow. Questions that are self-explanatory
// (e.g. foreign branches/subsidiaries) intentionally have no entry.
export const PROFILE_QUESTION_HELP: Partial<Record<ProfileQuestionKey, string>> = {
  a01:
    'ATF regulates importing firearms, ammunition, and explosives (the U.S. Munitions Import List). Choose yes if you import guns, gun parts, ammunition, or explosives into the U.S. Companies that don’t deal in those are usually no.',
  a02:
    'ITAR covers items and technical data on the U.S. Munitions List — military vehicle/aircraft parts, firearms, night-vision, military encryption — plus defense services and brokering. Choose yes if you make, sell, repair, or hold such items or their drawings and specs, or you’re registered with DDTC.',
  a03:
    'These are DDTC-approved licenses to share ITAR data or services with foreign parties: a Technical Assistance Agreement (TAA), Manufacturing License Agreement (MLA), or Warehouse & Distribution Agreement (WDA). Choose yes only if you’ve signed one. If ITAR doesn’t apply to you, this is no.',
  a04:
    'NISPOM applies to companies that access U.S. government classified information (CONFIDENTIAL, SECRET, TOP SECRET). Choose yes if you hold a Facility Security Clearance or work on classified contracts. Controlled-but-unclassified (CUI) work alone belongs to the DOD Cyber question, not here.',
  a05:
    'DFARS clause 252.204-7012 applies when DoD contracts involve Covered Defense Information / CUI or operationally critical support. Choose yes if you hold DoD contracts that aren’t solely for commercial off-the-shelf items, or that require safeguarding CUI or NIST SP 800-171 compliance.',
  a06:
    'An export is sending anything subject to U.S. controls out of the country — hardware, software, or technical data. Choose yes if you ship products, parts, or samples abroad, send drawings or specs to foreign recipients, or carry a work laptop overseas. Returns and repairs count too.',
  a07:
    'In a routed export, the foreign buyer — not you — appoints a U.S. forwarding agent and takes on export-control responsibility, then asks you for information to file the export record. Choose yes if foreign customers arrange freight and authorization themselves. If you control your own exports, this is no.',
  a08:
    'This asks whether you act as a freight forwarder or customs agent arranging exports for other companies. Choose yes only if forwarding is part of your business. Simply using a forwarder for your own shipments is no.',
  a09:
    'Covers moving items or technical data across a U.S. border in either direction — shipments out and shipments in — including transfers to or from your own foreign sites. Choose yes if goods or data physically enter or leave the country.',
  a10:
    'A hand carry is an employee personally taking items or data abroad — a laptop, phone, prototype, or samples. A laptop holding controlled technical data is itself an export the moment it crosses a border. Choose yes if employees travel internationally with company hardware or data.',
  a11:
    'Choose yes if you create, receive, or store export-controlled software or technical data — controlled drawings on a server or in the cloud, or hardware that reveals controlled know-how. Note that a foreign person accessing that data inside the U.S. can be a “deemed export.”',
  a13:
    'A foreign person is anyone who isn’t a U.S. citizen, lawful permanent resident (green-card holder), or protected individual — visa and work-permit holders included. Choose yes if you employ any. Giving them access to controlled technology can be a “deemed export” that needs a license.',
};

/** The topic prefix before the first colon, e.g. "ITAR" — for compact lists. */
export function questionShortLabel(key: ProfileQuestionKey): string {
  const text = PROFILE_QUESTIONS[key];
  const colon = text.indexOf(':');
  return colon > 0 ? text.slice(0, colon) : text;
}

type ProfileAnswers = Partial<Pick<Company, ProfileQuestionKey>>;

/** True once the company has answered at least one profile question. */
export function hasAnyProfileAnswer(
  company: ProfileAnswers | null | undefined,
): boolean {
  if (!company) return false;
  return PROFILE_QUESTION_KEYS.some(
    (k) => company[k] !== null && company[k] !== undefined,
  );
}

/** How many of the profile questions have a recorded yes/no answer. */
export function profileAnswerCounts(company: ProfileAnswers | null | undefined): {
  answered: number;
  total: number;
} {
  const total = PROFILE_QUESTION_KEYS.length;
  if (!company) return { answered: 0, total };
  const answered = PROFILE_QUESTION_KEYS.filter(
    (k) => company[k] !== null && company[k] !== undefined,
  ).length;
  return { answered, total };
}
